import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import './App.css';
import LoginPage from './components/LoginPage';
import PendingApprovalScreen from './components/PendingApprovalScreen';
import RejectedScreen from './components/RejectedScreen';
import Navigation from './components/Navigation';
import CatechesesPage from './pages/CatechesesPage';
import GemeenschappenPage from './pages/GemeenschappenPage';
import ConviventiesPage from './pages/ConviventiesPage';
import LedenPage from './pages/LedenPage';
import GebruikersPage from './pages/GebruikersPage';
import UserDashboardPage from './pages/UserDashboardPage';
import ReleaseNotesPage from './pages/ReleaseNotesPage';
import { useAuth } from './context/AuthContext';
import { trackPageView, setUserRole } from './services/analytics';

/**
 * RouteTracker fires a GA4 page_view event on every route transition.
 * Must be rendered inside <BrowserRouter>.
 */
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

/**
 * Main application component for Etappes en Catechese
 * Provides routing and navigation for the app
 */
function AppContent() {
  // Auth state
  const { user, isAdmin, userProfile, isRejected, isPending, loading: authLoading, logout } = useAuth();
  const appVersion = process.env.REACT_APP_VERSION || '1.x.x';

  // Sync user role to GA4 whenever the profile changes
  useEffect(() => {
    if (!user) {
      setUserRole(null);
      return;
    }
    const role = isAdmin ? 'admin' : (userProfile?.status === 'approved' ? 'user' : null);
    setUserRole(role);
  }, [user, isAdmin, userProfile]);

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="App">
        <div className="auth-loading">Laden...</div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return <LoginPage />;
  }

  // Show pending screen if user is awaiting approval
  if (isPending) {
    return <PendingApprovalScreen />;
  }

  // Show rejected screen if user was rejected
  if (isRejected) {
    return <RejectedScreen />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Etappes en Catecheses</h1>
        <div className="header-user">
          <Link to="/dashboard" className="header-user-link" title="Mijn overzicht">
            <span className="user-email">{user.email}</span>
            {isAdmin && <span className="admin-badge">Admin</span>}
          </Link>
          <button className="logout-button" onClick={logout}>
            Uitloggen
          </button>
        </div>
      </header>
      <Navigation />
      <main className="App-main">
        <Routes>
          <Route path="/" element={<Navigate to="/catecheses" replace />} />
          <Route path="/catecheses" element={<CatechesesPage />} />
          <Route path="/gemeenschappen" element={<GemeenschappenPage />} />
          <Route path="/dashboard" element={<UserDashboardPage />} />
          <Route path="/release-notes" element={<ReleaseNotesPage />} />
          <Route path="/conviventies" element={<ConviventiesPage />} />
          <Route path="/leden" element={<LedenPage />} />
          <Route 
            path="/gebruikers" 
            element={isAdmin ? <GebruikersPage /> : <Navigate to="/catecheses" replace />} 
          />
          <Route path="*" element={<Navigate to="/catecheses" replace />} />
        </Routes>
      </main>
      <footer className="App-footer">
        Etappes en Catecheses version {appVersion} | <Link to="/release-notes" className="app-footer-link">Wat is nieuw</Link> | Federico Calore
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <RouteTracker />
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
